import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { UploadFile } from '@components/common/upload';
import { ProfileResumeAdditionInfoSaveDto } from '@domain/rq/match/MatchingApplicationApplyRq';
import { AdditionInfoVO } from '@domain/vo/match/ResumeAdditionInfoVO';
import { deepCopy } from '@utils/common';

export type ChoiceType = { name: string; selectYn: boolean };

export type FormDataType = Record<number, string | string[] | { label: string; value: string } | UploadFile[]>;

interface AdditionalInputContextType {
  formData: ProfileResumeAdditionInfoSaveDto[];
  previewData: ProfileResumeAdditionInfoSaveDto[];
  tempFormData: FormDataType;
  isComplete: boolean;

  saveTempData: (form: FormDataType) => void;
  savePreviewData: (form: FormDataType, additionInfo: AdditionInfoVO[]) => void;
  saveFormData: (form: FormDataType, additionInfo: AdditionInfoVO[]) => void;
  clearForm: () => void;
  setIsCompleteFalse: () => void;
}

const AdditionalInputContext = createContext<AdditionalInputContextType>({
  formData: [],
  previewData: [],
  tempFormData: {},
  isComplete: false,

  saveTempData: () => {},
  savePreviewData: () => {},
  saveFormData: () => {},
  clearForm: () => {},
  setIsCompleteFalse: () => {},
});

export const AdditionalInputProvider = ({ children }: { children: React.ReactNode }) => {
  const tempFormData = useRef<FormDataType>({}); // 저장 또는 나가기 관련 임시 데이터
  const formData = useRef<ProfileResumeAdditionInfoSaveDto[]>([]); // 저장 또는 나가기 관련 임시 데이터
  const previewData = useRef<ProfileResumeAdditionInfoSaveDto[]>([]); // 미리보기 데이터
  const [isComplete, setIsComplete] = useState(false);

  const saveTempData = useCallback((formData: FormDataType) => {
    tempFormData.current = deepCopy(formData);
  }, []);

  const saveFormData = useCallback((form: FormDataType, additionInfo: AdditionInfoVO[]) => {
    const forms = parsingFormData({ formData: form, additionInfo, type: 'rq' });
    formData.current = forms;
    setIsComplete(true);
  }, []);

  const savePreviewData = useCallback((form: FormDataType, additionInfo: AdditionInfoVO[]) => {
    const forms = parsingFormData({ formData: form, additionInfo, type: 'preview' });
    previewData.current = forms;
  }, []);

  const clearForm = useCallback(() => {
    formData.current = [];
    previewData.current = [];
    tempFormData.current = {};
    setIsComplete(false);
  }, []);

  const setIsCompleteFalse = useCallback(() => {
    setIsComplete(false);
  }, []);

  return (
    <AdditionalInputContext.Provider
      value={{
        formData: formData.current,
        previewData: previewData.current,
        tempFormData: tempFormData.current,
        isComplete,

        saveFormData,
        saveTempData,
        savePreviewData,
        clearForm,
        setIsCompleteFalse,
      }}
    >
      {children}
    </AdditionalInputContext.Provider>
  );
};

export const useAdditionalInputContext = () => {
  const context = useContext(AdditionalInputContext);
  if (!context) throw new Error('AdditionalInputContext is not defined');

  return context;
};

const FormContext = createContext<UseFormReturn<FormDataType> | null>(null);

export const AdditionInfoFormProvider = ({ children }: { children: React.ReactNode }) => {
  const methods = useForm<FormDataType>();

  return (
    <FormContext.Provider value={methods}>
      <AdditionalInputProvider>{children}</AdditionalInputProvider>
    </FormContext.Provider>
  );
};

export const useAdditionInfoHookFormContext = () => {
  const context = useContext(FormContext);
  if (!context) throw new Error('AdditionInfoFormContext is not defined');

  return context;
};

const parsingFormData = ({
  formData,
  additionInfo,
  type,
}: {
  formData: FormDataType;
  additionInfo: AdditionInfoVO[];
  type: 'rq' | 'preview';
}) => {
  const forms = Object.entries(formData).map(([key, value]) => {
    const infoSn = Number(key);
    const info = additionInfo.find((info) => info.additionInfoSn === infoSn);
    if (!info) return;

    if (type === 'preview' && !value) return {};

    switch (info.formType) {
      case 'TEXT':
        return {
          question: info.question,
          answer: value,
          type: info.formType,
        };
      case 'SINGLE_CHOICE':
        return {
          question: info.question,
          choices: info.choices.map((choice) => ({
            name: choice,
            selectYn: value === choice,
          })),
          type: info.formType,
        };
      case 'MULTI_CHOICE':
        return {
          question: info.question,
          choices: info.choices.map((choice) => ({
            name: choice,
            selectYn: (value as string[])?.includes(choice),
          })),
          type: info.formType,
        };
      case 'DROPDOWN':
        return {
          question: info.question,
          choices: info.choices.map((choice) => ({
            name: choice,
            selectYn: (value as { label: string; value: string })?.value === choice,
          })),
          type: info.formType,
        };
      case 'ADDITION_ATTACH_FILE':
        return {
          question: info.question,
          files: type === 'rq' ? (value as UploadFile[])?.map((file) => file.fileUid) : value,
          type: info.formType,
        };
    }
  }) as ProfileResumeAdditionInfoSaveDto[];

  return forms;
};
